import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Layers, Plus, Target, Trash2, Columns } from 'lucide-react';
import api from '../../services/api';
import alertService from '../../utils/alert';
import { cn } from '../../utils/cn';
import { formatCurrency } from '../../utils/currency';
import { Btn, INPUT, LABEL, errMsg } from '../../components/crm/billing/ui';
import { resetAccountingLists } from '../../components/crm/billing/ItemPicker';
import Tabs from '../../components/ui/Tabs';
import PageHeader from '../../components/PageHeader';
import { PAGE } from '../../components/crm/ui';

const TABS = [
  { key: 'budgets', label: 'Budgets', icon: Target },
  { key: 'segments', label: 'P&L by cost centre', icon: Columns },
  { key: 'centres', label: 'Cost centres', icon: Layers },
];
const monthLabel = (m) => new Date(`${m}-01T00:00:00`).toLocaleDateString('en-GB', { month: 'short' });
const fyStartDefault = () => { const d = new Date(); const y = d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1; return `${y}-04`; };

function CentresTab({ centres, reload }) {
  const [f, setF] = useState({ name: '', code: '', kind: 'department' });
  const save = async (id, body) => {
    try {
      if (id) await api.put(`/crm/accounting/cost-centres/${id}`, body); else { await api.post('/crm/accounting/cost-centres', body); setF({ name: '', code: '', kind: 'department' }); }
      resetAccountingLists(); reload();
    } catch (e) { alertService.error('Not saved', errMsg(e)); }
  };
  return (
    <div className="flex flex-col gap-3 max-w-3xl">
      <p className="text-[12px] text-[#706E6B]">Tag invoices, bill lines, journal lines, assets and expense claims with a department, project or branch to see profit for each.</p>
      <div className="flex flex-wrap items-end gap-2 bg-white border border-[#E5E5E5] rounded-lg p-3 shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
        <div className="flex-1 min-w-[180px]"><label className={LABEL} htmlFor="cc-name">Name</label><input id="cc-name" className={INPUT} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
        <div className="w-28"><label className={LABEL} htmlFor="cc-code">Code</label><input id="cc-code" className={INPUT} value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} /></div>
        <div className="w-36"><label className={LABEL} htmlFor="cc-kind">Kind</label><select id="cc-kind" className={INPUT} value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })}>{['department', 'project', 'branch', 'other'].map((k) => <option key={k} value={k} className="capitalize">{k}</option>)}</select></div>
        <Btn primary disabled={!f.name.trim()} onClick={() => save(null, f)}><Plus size={14} className="inline -mt-0.5 mr-1" />Add</Btn>
      </div>
      <div className="bg-white border border-[#E5E5E5] rounded-lg divide-y divide-[#EEF1F6] shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
        {centres.map((c) => (
          <div key={c._id} className={cn('flex items-center justify-between px-3 py-2 text-[13px]', !c.isActive && 'opacity-50')}>
            <span><strong>{c.name}</strong>{c.code && <span className="text-[#706E6B]"> · {c.code}</span>} <span className="text-[11px] text-[#706E6B] capitalize">{c.kind}</span></span>
            <button type="button" className="text-[12px] text-[#706E6B] hover:underline" onClick={() => save(c._id, { ...c, isActive: !c.isActive })}>{c.isActive ? 'Deactivate' : 'Reactivate'}</button>
          </div>
        ))}
        {!centres.length && <p className="text-[13px] text-[#706E6B] text-center py-6">No cost centres yet.</p>}
      </div>
    </div>
  );
}

function SegmentsTab() {
  const [range, setRange] = useState({ from: '', to: '' });
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get('/crm/accounting/segment-pnl', { params: { from: range.from || undefined, to: range.to || undefined } }).then((r) => setD(r.data)).catch((e) => alertService.error('Could not load', errMsg(e)));
  }, [range]);
  if (!d) return <p className="text-[13px] text-[#706E6B]">Loading…</p>;
  const cols = d.columns.filter((c) => c.id === '' || [...d.income, ...d.expenses].some((r) => r.values[c.id]));
  const money = (v) => (v ? formatCurrency(v, d.currency) : '—');
  const section = (title, rows) => (
    <>
      <tr className="bg-[#F8FAFC]"><td colSpan={cols.length + 2} className="px-3 py-1.5 text-[11px] uppercase tracking-wide text-[#706E6B] font-semibold">{title}</td></tr>
      {rows.map((r) => (
        <tr key={r.accountId} className="border-t border-[#F3F2F2]">
          <td className="px-3 py-1.5">{r.code} · {r.name}</td>
          {cols.map((c) => <td key={c.id} className="px-3 py-1.5 text-right tabular-nums">{money(r.values[c.id] * (r.type === 'contra_revenue' ? -1 : 1))}</td>)}
          <td className="px-3 py-1.5 text-right tabular-nums font-semibold">{money(r.total * (r.type === 'contra_revenue' ? -1 : 1))}</td>
        </tr>
      ))}
    </>
  );
  return (
    <div className="flex flex-col gap-3">
      <div className="flex gap-2">
        <div><label className={LABEL} htmlFor="sg-from">From</label><input id="sg-from" type="date" className={INPUT} value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })} /></div>
        <div><label className={LABEL} htmlFor="sg-to">To</label><input id="sg-to" type="date" className={INPUT} value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} /></div>
      </div>
      <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] overflow-x-auto">
        <table className="w-full text-[12px]" style={{ minWidth: 320 + cols.length * 130 }}>
          <thead className="text-[#706E6B] text-[11px] uppercase tracking-wide"><tr><th className="px-3 py-2 text-left">Account</th>{cols.map((c) => <th key={c.id} className="px-3 py-2 text-right">{c.name}</th>)}<th className="px-3 py-2 text-right">Total</th></tr></thead>
          <tbody>
            {section('Income', d.income)}
            {section('Expenses', d.expenses)}
            <tr className="border-t-2 border-[#D8DDE6] font-semibold">
              <td className="px-3 py-2">Net profit</td>
              {cols.map((c) => <td key={c.id} className={cn('px-3 py-2 text-right tabular-nums', d.netProfit[c.id] < 0 && 'text-[#BA0517]')}>{formatCurrency(d.netProfit[c.id] || 0, d.currency)}</td>)}
              <td className={cn('px-3 py-2 text-right tabular-nums', d.netProfit.total < 0 && 'text-[#BA0517]')}>{formatCurrency(d.netProfit.total, d.currency)}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p className="text-[12px] text-[#706E6B]">“Not assigned” is income and spending without a cost centre. Tax and balance-sheet accounts are not included.</p>
    </div>
  );
}

function BudgetEditor({ budget, accounts, centres, onSaved, onCancel }) {
  const [f, setF] = useState(() => ({
    name: budget?.name || '', yearStart: budget ? new Date(budget.yearStart).toISOString().slice(0, 7) : fyStartDefault(),
    costCentreId: budget?.costCentreId?._id || budget?.costCentreId || '',
    lines: budget?.lines?.map((l) => ({ accountId: l.accountId, amounts: l.amounts.map(String) })) || [],
  }));
  const [saving, setSaving] = useState(false);
  const months = useMemo(() => {
    const [y, m] = f.yearStart.split('-').map(Number);
    return Array.from({ length: 12 }, (_, i) => new Date(y, m - 1 + i, 1).toLocaleDateString('en-GB', { month: 'short' }));
  }, [f.yearStart]);
  const pl = accounts.filter((a) => ['revenue', 'contra_revenue', 'expense'].includes(a.type));
  const setAmt = (li, mi, v) => setF({ ...f, lines: f.lines.map((l, i) => (i === li ? { ...l, amounts: l.amounts.map((a, j) => (j === mi ? v : a)) } : l)) });
  const spread = (li) => {
    const total = Number(window.prompt('Yearly amount to spread evenly over 12 months') || 0);
    if (total > 0) setF({ ...f, lines: f.lines.map((l, i) => (i === li ? { ...l, amounts: Array.from({ length: 12 }, (_, j) => String(j < 11 ? Math.round((total / 12) * 100) / 100 : Math.round((total - Math.round((total / 12) * 100) / 100 * 11) * 100) / 100)) } : l)) });
  };
  const save = async () => {
    setSaving(true);
    try {
      const body = { ...f, yearStart: `${f.yearStart}-01`, costCentreId: f.costCentreId || null, lines: f.lines.filter((l) => l.accountId).map((l) => ({ accountId: l.accountId, amounts: l.amounts.map(Number) })) };
      if (budget?._id) await api.put(`/crm/accounting/budgets/${budget._id}`, body); else await api.post('/crm/accounting/budgets', body);
      onSaved();
    } catch (e) { alertService.error('Budget not saved', errMsg(e)); } finally { setSaving(false); }
  };
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-end gap-2">
        <div className="w-56"><label className={LABEL} htmlFor="bd-name">Name</label><input id="bd-name" className={INPUT} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="FY 2026-27" /></div>
        <div><label className={LABEL} htmlFor="bd-start">Year starts</label><input id="bd-start" type="month" className={INPUT} value={f.yearStart} onChange={(e) => setF({ ...f, yearStart: e.target.value })} /></div>
        <div className="w-52"><label className={LABEL} htmlFor="bd-cc">For</label><select id="bd-cc" className={INPUT} value={f.costCentreId} onChange={(e) => setF({ ...f, costCentreId: e.target.value })}><option value="">Whole company</option>{centres.map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}</select></div>
        <div className="flex-1" />
        <Btn onClick={onCancel}>Cancel</Btn><Btn primary disabled={saving} onClick={save}>{saving ? 'Saving…' : 'Save budget'}</Btn>
      </div>
      <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] overflow-x-auto">
        <table className="text-[12px]" style={{ minWidth: 1300 }}>
          <thead className="text-[#706E6B] text-[11px] uppercase tracking-wide"><tr><th className="px-2 py-2 text-left w-64">Account</th>{months.map((m) => <th key={m} className="px-1 py-2 text-right w-20">{m}</th>)}<th className="px-2 py-2 text-right">Year</th><th /></tr></thead>
          <tbody>
            {f.lines.map((l, li) => (
              <tr key={li} className="border-t border-[#F3F2F2]">
                <td className="px-2 py-1"><select aria-label="Account" className={INPUT} value={l.accountId} onChange={(e) => setF({ ...f, lines: f.lines.map((x, i) => (i === li ? { ...x, accountId: e.target.value } : x)) })}><option value="">Choose…</option>{pl.map((a) => <option key={a._id} value={a._id}>{a.code} · {a.name}</option>)}</select></td>
                {l.amounts.map((a, mi) => <td key={mi} className="px-1 py-1"><input aria-label={`${months[mi]} amount`} type="number" min="0" className={cn(INPUT, 'text-right px-1')} value={a} onChange={(e) => setAmt(li, mi, e.target.value)} /></td>)}
                <td className="px-2 py-1 text-right tabular-nums font-semibold">{l.amounts.reduce((s, v) => s + (Number(v) || 0), 0).toLocaleString('en-IN')}</td>
                <td className="px-1 whitespace-nowrap"><button type="button" className="text-[11px] text-[#0176D3] hover:underline mr-1" onClick={() => spread(li)}>Spread</button><button type="button" aria-label="Remove line" className="p-1 text-[#706E6B] hover:text-[#BA0517]" onClick={() => setF({ ...f, lines: f.lines.filter((_, i) => i !== li) })}><Trash2 size={13} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div><Btn onClick={() => setF({ ...f, lines: [...f.lines, { accountId: '', amounts: Array(12).fill('') }] })}><Plus size={13} className="inline -mt-0.5 mr-1" />Add account</Btn></div>
    </div>
  );
}

function BudgetsTab({ centres }) {
  const [list, setList] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [view, setView] = useState(null);
  const load = useCallback(() => api.get('/crm/accounting/budgets').then((r) => setList(r.data.budgets || [])).catch(() => {}), []);
  useEffect(() => { load(); api.get('/crm/bookkeeping/accounts').then((r) => setAccounts(r.data.accounts || [])).catch(() => {}); }, [load]);
  const open = (id) => api.get(`/crm/accounting/budgets/${id}/vs-actual`).then((r) => setView(r.data)).catch((e) => alertService.error('Could not load', errMsg(e)));
  const edit = async (id) => { try { const r = await api.get(`/crm/accounting/budgets/${id}`); setEditing(r.data.budget); } catch (e) { alertService.error('Could not open', errMsg(e)); } };
  const remove = async (b) => {
    if (!(await alertService.confirm(`Delete ${b.name}?`, 'The budget is removed; your books are not affected.', 'warning', 'Delete'))) return;
    try { await api.delete(`/crm/accounting/budgets/${b._id}`); setView(null); load(); } catch (e) { alertService.error('Not deleted', errMsg(e)); }
  };
  if (editing) return <BudgetEditor budget={editing._id ? editing : null} accounts={accounts} centres={centres} onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />;
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-end gap-2">
        <div className="w-72"><label className={LABEL} htmlFor="bd-pick">Budget</label>
          <select id="bd-pick" className={INPUT} value={view?.budget?._id || ''} onChange={(e) => e.target.value && open(e.target.value)}>
            <option value="">Choose…</option>{list.map((b) => <option key={b._id} value={b._id}>{b.name} · {b.costCentreId?.name || 'Whole company'}</option>)}
          </select></div>
        {view && <><Btn onClick={() => edit(view.budget._id)}>Edit</Btn><Btn danger onClick={() => remove(view.budget)}>Delete</Btn></>}
        <div className="flex-1" />
        <Btn primary onClick={() => setEditing({})}><Plus size={14} className="inline -mt-0.5 mr-1" />New budget</Btn>
      </div>
      {!view && <p className="text-[13px] text-[#706E6B]">{list.length ? 'Choose a budget to compare it with actual figures.' : 'Set a monthly budget per income and expense account, for the company or one cost centre, then track actuals against it.'}</p>}
      {view && (
        <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] overflow-x-auto">
          <table className="text-[12px] w-full" style={{ minWidth: 1100 }}>
            <thead className="text-[#706E6B] text-[11px] uppercase tracking-wide"><tr><th className="px-3 py-2 text-left">Account</th>{view.months.map((m) => <th key={m} className="px-2 py-2 text-right">{monthLabel(m)}</th>)}<th className="px-3 py-2 text-right">Budget to date</th><th className="px-3 py-2 text-right">Actual to date</th><th className="px-3 py-2 text-right">Variance</th></tr></thead>
            <tbody>
              {view.rows.map((r) => (
                <tr key={r.accountId} className="border-t border-[#F3F2F2] align-top">
                  <td className="px-3 py-2">{r.code} · {r.name}</td>
                  {view.months.map((m, i) => (
                    <td key={m} className="px-2 py-2 text-right tabular-nums">
                      <span className="block">{Math.round(r.actual[i]).toLocaleString('en-IN')}</span>
                      <span className="block text-[10px] text-[#706E6B]">of {Math.round(r.budget[i]).toLocaleString('en-IN')}</span>
                    </td>
                  ))}
                  <td className="px-3 py-2 text-right tabular-nums">{formatCurrency(r.budgetToDate, view.currency)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{formatCurrency(r.actualToDate, view.currency)}</td>
                  <td className={cn('px-3 py-2 text-right tabular-nums font-semibold', r.favourable ? 'text-[#2E844A]' : 'text-[#BA0517]')}>
                    {formatCurrency(r.variance, view.currency)}{r.variancePct !== null && <span className="block text-[10px] font-normal">{r.variancePct > 0 ? '+' : ''}{r.variancePct}%</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-[11px] text-[#706E6B] px-3 py-2">Each month shows actual over budget. Green: income above or costs below budget so far this year.</p>
        </div>
      )}
    </div>
  );
}

export default function BudgetsPage() {
  const [tab, setTab] = useState('budgets');
  const [centres, setCentres] = useState([]);
  const loadCentres = useCallback(() => api.get('/crm/accounting/cost-centres', { params: { active: 'all' } }).then((r) => setCentres(r.data.costCentres || [])).catch(() => {}), []);
  useEffect(() => { loadCentres(); }, [loadCentres]);
  return (
    <div className={PAGE}>
      <PageHeader title="Budgets & cost centres" subtitle="Plan by month, and see profit for each department or project." icon={Target} />
      <Tabs tabs={TABS} value={tab} onChange={setTab} />
      {tab === 'budgets' && <BudgetsTab centres={centres.filter((c) => c.isActive)} />}
      {tab === 'segments' && <SegmentsTab />}
      {tab === 'centres' && <CentresTab centres={centres} reload={loadCentres} />}
    </div>
  );
}
